// src/pages/Transactions.tsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { getTransactions } from "../redux/slices/transactionSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  Search,
  ArrowDownRight,
  ArrowUpRight,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
} from "lucide-react";
import toast from "react-hot-toast";
import { formatFiat } from "../utils/currency";

const Transactions: React.FC = () => {
  const dispatch = useDispatch();
  const { transactions, isLoading } = useSelector(
    (state: RootState) => state.transactions
  );
  const [filter, setFilter] = useState({
    type: "",
    status: "",
    search: "",
  });

  useEffect(() => {
    dispatch(getTransactions() as any);
  }, [dispatch]);

  const filteredTransactions = transactions.filter((transaction) => {
    if (filter.type && transaction.type !== filter.type) return false;
    if (filter.status && transaction.status !== filter.status) return false;
    if (filter.search) {
      const searchLower = filter.search.toLowerCase();
      return (
        transaction.tx_hash?.toLowerCase().includes(searchLower) ||
        transaction.wallet_address?.toLowerCase().includes(searchLower) ||
        transaction.type.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const handleRefresh = () => {
    dispatch(getTransactions() as any);
    toast.success("Transactions refreshed");
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "confirmed":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "pending":
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case "rejected":
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className={`${GlobalStyles.spinner} h-8 w-8`}></div>
      </div>
    );
  }

  return (
    <div className={GlobalStyles.pageContainer}>
      <div className={GlobalStyles.contentContainer}>
        {/* Header */}
        <div className={GlobalStyles.sectionSpacing}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className={GlobalStyles.heading1}>Transactions</h1>
              <p className={GlobalStyles.bodyText + " mt-2"}>
                View all your transactions
              </p>
            </div>
            <button
              onClick={handleRefresh}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium}`}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className={`${GlobalStyles.card} ${GlobalStyles.sectionSpacing}`}>
          <div className={`${GlobalStyles.gridCols3} gap-4 mb-4`}>
            <div>
              <label className={GlobalStyles.inputLabel}>Type</label>
              <select
                value={filter.type}
                onChange={(e) => setFilter({ ...filter, type: e.target.value })}
                className={GlobalStyles.input}
              >
                <option value="">All Types</option>
                <option value="deposit">Deposit</option>
                <option value="withdrawal">Withdrawal</option>
                <option value="bonus">Bonus</option>
              </select>
            </div>
            <div>
              <label className={GlobalStyles.inputLabel}>Status</label>
              <select
                value={filter.status}
                onChange={(e) =>
                  setFilter({ ...filter, status: e.target.value })
                }
                className={GlobalStyles.input}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div>
              <label className={GlobalStyles.inputLabel}>Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search transactions..."
                  value={filter.search}
                  onChange={(e) =>
                    setFilter({ ...filter, search: e.target.value })
                  }
                  className={`${GlobalStyles.input} pl-10`}
                />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {filteredTransactions.length} transactions found
            </p>
            <button
              onClick={() => setFilter({ type: "", status: "", search: "" })}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} ${GlobalStyles.buttonSmall}`}
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Transactions Table */}
        <div className={GlobalStyles.card}>
          <div className="overflow-x-auto">
            <table className={GlobalStyles.table}>
              <thead>
                <tr className={`${GlobalStyles.tableHeader} text-left`}>
                  <th className={`${GlobalStyles.tableCell} w-32`}>Type</th>
                  <th className={GlobalStyles.tableCell}>Amount</th>
                  <th className={GlobalStyles.tableCell}>Status</th>
                  <th className={GlobalStyles.tableCell}>Transaction Hash</th>
                  <th className={GlobalStyles.tableCell}>Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.length > 0 ? (
                  filteredTransactions.map((transaction) => (
                    <tr key={transaction.id} className={GlobalStyles.tableRow}>
                      <td className={GlobalStyles.tableCell}>
                        <div className="flex items-center">
                          {transaction.type === "deposit" ? (
                            <ArrowDownRight className="h-4 w-4 text-green-500 mr-2" />
                          ) : (
                            <ArrowUpRight className="h-4 w-4 text-red-500 mr-2" />
                          )}
                          <span className="font-medium capitalize text-black dark:text-white">
                            {transaction.type}
                          </span>
                        </div>
                      </td>
                      <td className={GlobalStyles.tableCell}>
                        <div
                          className={`font-medium ${
                            transaction.type === "deposit"
                              ? "text-green-600 dark:text-green-400"
                              : transaction.type === "withdrawal"
                              ? "text-red-600 dark:text-red-400"
                              : "text-purple-600 dark:text-purple-400"
                          }`}
                        >
                          {transaction.type === "deposit" ? "+" : "-"}
                          {formatFiat(transaction.amount)}
                        </div>
                      </td>
                      <td className={GlobalStyles.tableCell}>
                        <div className="flex items-center">
                          {getStatusIcon(transaction.status)}
                          <span
                            className={`${GlobalStyles.badge} ml-2 ${
                              transaction.status === "confirmed"
                                ? GlobalStyles.badgeSuccess
                                : transaction.status === "pending"
                                ? GlobalStyles.badgeWarning
                                : GlobalStyles.badgeError
                            }`}
                          >
                            {transaction.status}
                          </span>
                        </div>
                      </td>
                      <td className={GlobalStyles.tableCell}>
                        <div
                          className="font-mono text-sm truncate max-w-xs text-gray-500 dark:text-gray-400"
                          title={transaction.tx_hash || "N/A"}
                        >
                          {transaction.tx_hash || "N/A"}
                        </div>
                      </td>
                      <td className={GlobalStyles.tableCell}>
                        <span className="text-gray-500 dark:text-gray-400 text-xs">
                          {new Date(
                            transaction.created_at
                          ).toLocaleDateString()}
                        </span>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(
                            transaction.created_at
                          ).toLocaleTimeString()}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center">
                      <div className="text-center">
                        <div className="mx-auto w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                          <Search className="h-6 w-6 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                          No transactions found
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400">
                          {transactions.length === 0
                            ? "You have no transactions yet"
                            : "Try adjusting your filters"}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Transactions;
